import classNames from "classnames";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import * as bd from "../index";

type ScrollPaneApi = {
    canGoPrev: boolean;
    canGoNext: boolean;
    scrollTo: (elem) => void;
    isRtl: () => boolean;
    scroll: (delta: number) => void;
};

interface ScrollPaneProps {
    as?: any;
    vertical?: boolean;
    className?: string;
    containerClassName?: string;
    mouseWheel?: boolean;
    scrollDuration?: number;
    getApiRef?: MutableRefObject<() => ScrollPaneApi>;
    prevButton?: (api: ScrollPaneApi) => React.ReactNode;
    nextButton?: (api: ScrollPaneApi) => React.ReactNode;
    onWheel?: (e: any) => void;
    onScroll?: (e: any) => void;
    children: React.ReactNode;
    [x: string]: any;
}

export const ScrollPane = ({
    as,
    vertical,
    className,
    containerClassName,
    prevButton,
    nextButton,
    mouseWheel = true,
    children,
    onWheel,
    onScroll,
    getApiRef,
    ...props
}: ScrollPaneProps) => {
    if (getApiRef) getApiRef.current = getApi;
    const transition = new bd.Transition("easeOutQuart", 300);
    const ref = useRef(null);

    const isRtl = () => {
        if (vertical) return false;
        var elem = ref.current;
        var v = window.getComputedStyle ? window.getComputedStyle(elem, null).getPropertyValue("direction") : elem.currentStyle.direction;
        return v === "rtl";
    };

    const getScrollPos = () => (vertical ? ref.current.scrollTop : (isRtl() ? -1 : 1) * ref.current.scrollLeft);
    const getScrollSize = () => (vertical ? ref.current.scrollHeight : ref.current.scrollWidth);
    const getViewportSize = () => (vertical ? ref.current.clientHeight : ref.current.clientWidth);
    const setScrollPos = (v: number) => {
        if (vertical) ref.current.scrollTop = v;
        else ref.current.scrollLeft = isRtl() ? -v : v;
    };

    const scrollTo = (elem) => {
        const sign = isRtl() ? -1 : 1;
        const D = 40;
        var v1 = vertical ? ref.current.scrollTop : ref.current.scrollLeft;
        var v2 = v1 + getViewportSize();
        var e1 = vertical ? elem.offsetTop : elem.offsetLeft;
        var e2 = e1 + (vertical ? elem.clientHeight : elem.clientWidth);
        var delta: number = 0;
        if (e2 + D > v2) delta = v2 - e2 - D;
        if (e1 + delta - D < v1) delta += v1 - e1 + D;
        if (delta !== 0) scroll(-delta * sign);
    };

    const scroll = (delta) => {
        if (Math.abs(delta) < 1) delta = delta * getViewportSize();
        var curr = getScrollPos();
        var pos = (transition.busy ? transition.endPos : curr) + delta;
        pos = Math.max(0, Math.min(pos, getScrollSize() - getViewportSize()));
        transition.start(curr, pos, (value: number) => setScrollPos(value));
    };

    let [canGoPrev, setCanGoPrev] = useState(false);
    let [canGoNext, setCanGoNext] = useState(false);

    let updatePromise = null;
    function updateButtonState() {
        if (updatePromise) {
            return;
        }
        updatePromise = new Promise((resolve) => resolve(null));
        updatePromise.then(() => {
            updatePromise = null;
            var _prev = ref.current && getScrollPos() > 0;
            var _next = ref.current && getScrollPos() < getScrollSize() - getViewportSize();
            if (_prev === canGoPrev && _next === canGoNext) return;
            canGoPrev = _prev;
            canGoNext = _next;
            setCanGoPrev(_prev);
            setCanGoNext(_next);
        });
    }

    const handleOnWheel = (ev: React.WheelEvent) => {
        updateButtonState();
        if (onWheel) return onWheel(ev);
        if (!mouseWheel || vertical) return false;
        //ev.preventDefault();
        var delta = Math.min(150, getViewportSize() / 2);
        scroll(ev.deltaY > 0 ? delta : -delta);
    };

    const handleOnScroll = (ev) => {
        updateButtonState();
        if (onScroll) onScroll(ev);
    };

    useEffect(() => {
        updateButtonState();
    }, [ref]);

    const onResize = (w: number, h: number) => {
        updateButtonState();
    };

    function getApi() {
        return { canGoPrev, canGoNext, scroll, scrollTo, isRtl };
    }

    return (
        <div
            className={classNames("scroll-pane", className, {
                "scroll-pane-vertical": vertical,
                "scroll-pane-horizontal": !vertical,
            })}
            {...props}
        >
            {prevButton && prevButton({ canGoPrev, canGoNext, scroll, scrollTo, isRtl })}
            <bd.ResizeWatch
                as={as}
                dimension={vertical ? "height" : "width"}
                className={classNames("scroll-pane-container", containerClassName)}
                ref={ref}
                onWheel={handleOnWheel}
                onScroll={handleOnScroll}
                onResize={onResize}
            >
                {children}
            </bd.ResizeWatch>
            {nextButton && nextButton({ canGoPrev, canGoNext, scroll, scrollTo, isRtl })}
        </div>
    );
};
